<template>
    <div class="container">
        <!-- Heading Section -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h1 class="text-8 text-black mb-4">
                        Save Big on Power with Our Solar Panel Savings Calculator
                    </h1>
                    <p class="text-3">
                        The rising cost of electricity and the increasing demand for sustainable energy solutions make solar power an attractive option for homeowners and businesses alike. With Magetsi’s Solar Panel Savings Calculator, you can now estimate just how much you could save by switching to solar energy, empowering you to make informed decisions about your energy future.
                    </p>
                </div>
            </div>
        </div>

        <!-- Why Use a Solar Panel Savings Calculator -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h2 class="text-7 text-black mb-4">
                        Why Use a Solar Panel Savings Calculator?
                    </h2>
                    <p class="text-3">
                        Switching to solar energy is a significant investment, but understanding the potential savings can make it a no-brainer. Here’s why our Solar Panel Savings Calculator is a must-have tool:
                    </p>
                    <ul class="text-3">
                        <li><strong>Accurate Projections:</strong> Get precise estimates on how much money you can save on your electricity bills.</li>
                        <li><strong>Environmental Impact:</strong> See how switching to solar reduces your carbon footprint.</li>
                        <li><strong>Informed Decision-Making:</strong> Compare upfront costs with long-term savings to determine if solar is right for you.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- How the Solar Panel Savings Calculator Works -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h2 class="text-7 text-black mb-4">
                        How the Solar Panel Savings Calculator Works
                    </h2>
                    <p class="text-3">
                        Using the Solar Panel Savings Calculator is simple and straightforward. Just follow these steps:
                    </p>
                    <ol class="text-3">
                        <li><strong>Input Your Current Energy Usage:</strong> Enter your average monthly electricity consumption in kilowatt-hours (kWh).</li>
                        <li><strong>Solar System Efficiency Rate:</strong> Enter your system efficiency rate as a percentage.</li>
                        <li><strong>Calculate:</strong> Instantly view your potential savings.</li>
                    </ol>
                </div>
            </div>
        </div>

        <!-- Who Can Benefit from a Solar Panel Savings Calculator -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Who Can Benefit from a Solar Panel Savings Calculator?
                    </h3>
                    <ul class="text-3">
                        <li><strong>Homeowners:</strong> Determine the cost-effectiveness of installing solar panels on your property.</li>
                        <li><strong>Business Owners:</strong> Calculate savings on operational energy expenses by transitioning to solar power.</li>
                        <li><strong>Eco-Conscious Individuals:</strong> Quantify your environmental impact and make sustainable energy choices.</li>
                        <li><strong>Investors:</strong> Evaluate the return on investment (ROI) for solar projects.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- The Benefits of Solar Energy in Zimbabwe -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        The Benefits of Solar Energy in Zimbabwe
                    </h3>
                    <ul class="text-3">
                        <li><strong>Energy Independence:</strong> Reduce reliance on the national grid and avoid load shedding.</li>
                        <li><strong>Lower Energy Bills:</strong> Slash your electricity costs and save money in the long run.</li>
                        <li><strong>Sustainability:</strong> Contribute to a greener future by using renewable energy.</li>
                        <li><strong>Increased Property Value:</strong> Solar installations can boost the market value of your home or business.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Real-Life Scenarios for Using the Solar Panel Savings Calculator -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Real-Life Scenarios for Using the Solar Panel Savings Calculator
                    </h3>
                    <ul class="text-3">
                        <li><strong>Residential Solar Projects:</strong> Calculate how much you’ll save by powering your home with solar panels.</li>
                        <li><strong>Commercial Solar Installations:</strong> Estimate the financial and environmental benefits for your business.</li>
                        <li><strong>Rural Electrification:</strong> Plan solar projects for off-grid areas to provide reliable and sustainable energy.</li>
                        <li><strong>School and Community Projects:</strong> Evaluate savings for large-scale solar installations in educational or community spaces.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Understanding Solar Efficiency and Savings -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Understanding Solar Efficiency and Savings
                    </h3>
                    <p class="text-3">
                        The effectiveness of your solar system depends on several factors:
                    </p>
                    <ul class="text-3">
                        <li><strong>Solar Panel Efficiency:</strong> Higher efficiency panels generate more electricity from the same amount of sunlight.</li>
                        <li><strong>Location:</strong> Areas with more sunlight hours provide greater potential for energy production.</li>
                        <li><strong>System Size:</strong> Larger systems can produce more energy but may have a higher upfront cost.</li>
                        <li><strong>Energy Consumption:</strong> The more energy you use, the greater your potential savings.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Tips for Maximizing Your Solar Savings -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Tips for Maximizing Your Solar Savings
                    </h3>
                    <ul class="text-3">
                        <li><strong>Optimize Panel Placement:</strong> Ensure your solar panels are installed in a location that receives maximum sunlight.</li>
                        <li><strong>Use Energy-Efficient Appliances:</strong> Combine solar power with energy-saving devices for greater savings.</li>
                        <li><strong>Monitor Your System:</strong> Regularly check your solar system’s performance to ensure it operates efficiently.</li>
                        <li><strong>Consider Battery Storage:</strong> Invest in solar batteries to store excess energy for use during cloudy days or at night.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- The Environmental Impact of Solar Power -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        The Environmental Impact of Solar Power
                    </h3>
                    <p class="text-3">
                        Beyond the financial benefits, solar energy significantly reduces your carbon footprint. By switching to solar, you:
                    </p>
                    <ul class="text-3">
                        <li><strong>Reduce Greenhouse Gas Emissions:</strong> Lower your reliance on fossil fuels.</li>
                        <li><strong>Conserve Natural Resources:</strong> Minimize the use of non-renewable energy sources.</li>
                        <li><strong>Promote Sustainable Living:</strong> Set an example for others by adopting eco-friendly practices.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- How Magetsi’s Calculator Stands Out -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        How Magetsi’s Calculator Stands Out
                    </h3>
                    <ul class="text-3">
                        <li><strong>User-Friendly Interface:</strong> Easy to use, even for those new to solar energy.</li>
                        <li><strong>Mobile Compatibility:</strong> Access the calculator on your smartphone or tablet for convenience.</li>
                        <li><strong>Instant Results:</strong> Get detailed savings projections within seconds.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Button to Redirect User to the Calculator -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <button @click="scrollToTop()" class="btn btn-primary">
                        Take the First Step Today
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    methods: {
        scrollToTop() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }
}
</script>

<style scoped>
/* Add custom styles here */
.container {
    padding: 20px;
}

h1, h2, h3 {
    color: #333;
    font-size: 1.8rem;
}

.text-3 {
    font-size: 1.2rem;
}

.text-6 {
    font-size: 1.5rem;
}

.text-7 {
    font-size: 1.7rem;
}

.text-8 {
    font-size: 2rem;
}

button {
    margin-top: 20px;
}
</style>
